import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Box, Button, Card, Group, Loader, Progress, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconCheck, IconPlayerStop } from "@tabler/icons-react";
import { EasyAIError, runDemoChain, type ChainExecution, type RunProgress } from "@passio/easyai-client";
import { easyai, mealPlans } from "../lib/clients";
import { useSession } from "../lib/session";
import { ErrorNotice } from "./common";

/** A friendly step: done once every node up to `throughNode` (e.g. 4 = node-4) has finished. */
export interface RunStage {
  label: string;
  throughNode: number;
}

const nodeNum = (id: string) => Number(id.match(/(\d+)$/)?.[1] ?? 0);

/** Runs a registered chain by key through the proxy, tracking progress, cancel and errors. */
export function useChainRun() {
  const { me } = useSession();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [execution, setExecution] = useState<ChainExecution | null>(null);
  const [error, setError] = useState<unknown>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);

  const run = async (key: string, input: Record<string, unknown>): Promise<ChainExecution | null> => {
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    setError(null);
    setProgress(null);
    setRunning(true);
    try {
      const res = await runDemoChain({ easyai, mealPlans }, me?.app.easyAi.chains ?? [], key, input, { signal: ac.signal, onProgress: setProgress, maxPolls: 240 });
      setExecution(res.execution);
      return res.execution;
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setError(e);
      return null;
    } finally {
      if (abort.current === ac) abort.current = null;
      setRunning(false);
    }
  };

  return {
    run,
    cancel: () => abort.current?.abort(),
    reset: () => {
      setExecution(null);
      setError(null);
      setProgress(null);
    },
    running,
    progress,
    execution,
    error,
  };
}

/** Live progress for a chain run: named stages, elapsed time and a cancel button. */
export function RunProgressCard({
  title,
  progress,
  stages,
  onCancel,
  platformRoute,
}: {
  title: string;
  progress: RunProgress | null;
  stages: RunStage[];
  onCancel: () => void;
  /** Passio API meal plan type the run goes through, if any. */
  platformRoute?: string | null;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [t0] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const done = Math.max(0, ...(progress?.nodes ?? []).filter((n) => n.status === "completed").map((n) => nodeNum(n.nodeId)));
  const current = stages.findIndex((s) => s.throughNode > done);
  const total = Math.max(...stages.map((s) => s.throughNode));
  const pct = Number.isFinite(total) && total > 0 ? Math.min(100, (done / total) * 100) : current < 0 ? 100 : 0;
  return (
    <Card className="pd-run-card" role="status" aria-live="polite">
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <span className="pd-pulse" aria-hidden />
          <Text fw={700}>{current >= 0 ? stages[current].label : title}…</Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Badge variant="light" color="gray">
            {progress?.status ?? "starting"}
          </Badge>
          <Text fz="sm" c="dimmed" ff="monospace">
            {Math.round((now - t0) / 1000)}s
          </Text>
        </Group>
      </Group>
      <Progress value={Math.max(pct, 4)} color="lime.7" size="sm" radius="xl" mb="md" animated striped={pct < 100} aria-label="Overall progress" />
      <Stack gap={6} mb="sm">
        {stages.map((s, i) => {
          const state = current < 0 || i < current ? "done" : i === current ? "now" : "todo";
          return (
            <Group key={s.label} gap="sm" wrap="nowrap">
              {state === "done" ? (
                <ThemeIcon size={22} radius="xl" color="lime.8" variant="light">
                  <IconCheck size={14} />
                </ThemeIcon>
              ) : state === "now" ? (
                <Box w={22} h={22} style={{ display: "grid", placeItems: "center" }}>
                  <Loader size={16} color="lime.7" />
                </Box>
              ) : (
                <Box w={22} h={22} style={{ borderRadius: 99, border: "2px solid var(--mantine-color-default-border)" }} />
              )}
              <Text fz="sm" fw={state === "now" ? 650 : 400} c={state === "todo" ? "dimmed" : undefined}>
                {s.label}
              </Text>
            </Group>
          );
        })}
      </Stack>
      <Group justify="space-between">
        <Text fz="xs" c="dimmed">
          {platformRoute
            ? `Runs on the Passio API: POST /napi/tools/mealPlans/${platformRoute}, then poll the run every 1.5 s.`
            : "Runs as an EasyAI chain: execute, then poll the execution every 1.5 s."}
        </Text>
        <Button size="xs" variant="default" leftSection={<IconPlayerStop size={14} />} onClick={onCancel}>
          Stop waiting
        </Button>
      </Group>
    </Card>
  );
}

/** Chain failures show the execution's error; anything else goes through ErrorNotice. */
export function ChainError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  if (error instanceof EasyAIError && error.execution) {
    return (
      <Alert color="red" title={`The chain ${error.execution.status}`}>
        <Stack gap="xs">
          <Text fz="sm">{error.message}</Text>
          {onRetry && (
            <Button size="xs" variant="light" color="ink" w="fit-content" onClick={onRetry}>
              Try again
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }
  return <ErrorNotice error={error} onRetry={onRetry} />;
}
